/**
 * Firebase Configuration for PDF Statement Converter
 * Provides authentication, Firestore database, and storage services
 * Compatible with Firebase v9+ modular SDK and React 19
 */

import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, GoogleAuthProvider, setPersistence, browserLocalPersistence, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getAnalytics } from 'firebase/analytics';
import Logger from '../utils/logger';

// Firebase configuration object
// Note: These values are safe to be public as they're client-side identifiers
const firebaseConfig = {
  apiKey: "AIzaSyAVmt1JERIqqClUbProx7BiMuPgxbpYoC0",
  authDomain: "pdf-statement-converter.firebaseapp.com",
  projectId: "pdf-statement-converter",
  storageBucket: "pdf-statement-converter.firebasestorage.app",
  messagingSenderId: "53875826400",
  appId: "1:53875826400:web:fcba99edc19fb3e843fa85",
  measurementId: "G-QJ1W5BK8YW"
};

// Validate configuration
const validateConfig = (config) => {
  const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
  const missingFields = requiredFields.filter(field => !config[field] || config[field].includes('your-'));
  
  if (missingFields.length > 0) {
    Logger.warn('Firebase configuration incomplete. Missing or placeholder values for:', missingFields);
    Logger.warn('Please update src/firebase/config.js with your actual Firebase project configuration.');
  }
  
  return missingFields.length === 0;
};

// Initialize Firebase app
let app;
try {
  // Validate configuration before initialization
  const isConfigValid = validateConfig(firebaseConfig);
  
  if (!isConfigValid && process.env.NODE_ENV === 'production') {
    throw new Error('Invalid Firebase configuration in production environment');
  }
  
  app = initializeApp(firebaseConfig);
  Logger.firebase('Firebase app initialized successfully');
} catch (error) {
  Logger.firebase('Error initializing Firebase app:', error);
  throw new Error(`Firebase initialization failed: ${error.message}`);
}

// Initialize Firebase Authentication with persistence
let auth;
let authStateReady = false;
const authStatePromise = new Promise((resolve) => {
  // This will be resolved once auth state is determined
  window._authStateResolve = resolve;
});

try {
  auth = getAuth(app);
  
  // Set explicit persistence for cross-browser compatibility
  setPersistence(auth, browserLocalPersistence)
    .then(() => {
      Logger.auth('Firebase Auth persistence set to LOCAL');
      
      // Monitor auth state and resolve promise when ready
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (!authStateReady) {
          authStateReady = true;
          Logger.auth('Firebase Auth state ready:', user ? 'authenticated' : 'anonymous');
          if (window._authStateResolve) {
            window._authStateResolve(user);
            window._authStateResolve = null;
          }
          unsubscribe(); // Only need this for the initial state
        }
      });
    })
    .catch((error) => {
      Logger.auth('Error setting Firebase Auth persistence:', error);
      // Fallback - still resolve the promise
      if (window._authStateResolve) {
        window._authStateResolve(null);
        window._authStateResolve = null;
      }
    });
  
  // Connect to Auth emulator in development (optional)
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true') {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    Logger.auth('Connected to Firebase Auth emulator');
  }
  
  Logger.auth('Firebase Auth initialized successfully');
} catch (error) {
  Logger.auth('Error initializing Firebase Auth:', error);
  if (window._authStateResolve) {
    window._authStateResolve(null);
    window._authStateResolve = null;
  }
  throw new Error(`Firebase Auth initialization failed: ${error.message}`);
}

// Initialize Firestore Database
let db;
try {
  db = getFirestore(app);
  
  // Connect to Firestore emulator in development (optional)
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true') {
    connectFirestoreEmulator(db, 'localhost', 8080);
    Logger.firebase('Connected to Firestore emulator');
  }
  
  Logger.firebase('Firestore initialized successfully');
} catch (error) {
  Logger.firebase('Error initializing Firestore:', error);
  throw new Error(`Firestore initialization failed: ${error.message}`);
}

// Initialize Firebase Storage
let storage;
try {
  storage = getStorage(app);
  
  // Connect to Storage emulator in development (optional)
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true') {
    connectStorageEmulator(storage, 'localhost', 9199);
    Logger.firebase('Connected to Firebase Storage emulator');
  }
  
  Logger.firebase('Firebase Storage initialized successfully');
} catch (error) {
  Logger.firebase('Error initializing Firebase Storage:', error);
  throw new Error(`Firebase Storage initialization failed: ${error.message}`);
}

// Initialize Firebase Functions
let functions;
try {
  functions = getFunctions(app);
  
  // Connect to Functions emulator in development (optional)
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true') {
    connectFunctionsEmulator(functions, 'localhost', 5001);
    Logger.firebase('Connected to Firebase Functions emulator');
  }
  
  Logger.firebase('Firebase Functions initialized successfully');
} catch (error) {
  Logger.firebase('Error initializing Firebase Functions:', error);
  throw new Error(`Firebase Functions initialization failed: ${error.message}`);
}

// Initialize Firebase Analytics
let analytics;
try {
  // Only initialize analytics in browser environment
  if (typeof window !== 'undefined') {
    analytics = getAnalytics(app);
    Logger.firebase('Firebase Analytics initialized successfully');
  } else {
    Logger.firebase('Analytics skipped - server-side environment detected');
  }
} catch (error) {
  Logger.warn('Analytics initialization failed (non-critical):', error);
  analytics = null;
}

// Initialize Google Auth Provider
let googleProvider;
try {
  googleProvider = new GoogleAuthProvider();
  googleProvider.setCustomParameters({
    prompt: 'select_account'
  });
  Logger.auth('Google Auth Provider initialized successfully');
} catch (error) {
  Logger.auth('Error initializing Google Auth Provider:', error);
}

// Helper function to check if Firebase is properly configured
export const isFirebaseConfigured = () => {
  try {
    return validateConfig(firebaseConfig) && auth && db && storage && functions;
  } catch (error) {
    Logger.firebase('Firebase configuration check failed:', error);
    return false;
  }
};

// Helper function to get Firebase configuration status
export const getFirebaseStatus = () => {
  return {
    app: !!app,
    auth: !!auth,
    firestore: !!db,
    storage: !!storage,
    functions: !!functions,
    configured: isFirebaseConfigured()
  };
};

// Connection state checker (useful for debugging)
export const checkFirebaseConnection = async () => {
  const status = {
    auth: false,
    firestore: false,
    storage: false
  };

  try {
    // Check Auth connection
    if (auth) {
      await auth.authStateReady();
      status.auth = true;
    }
  } catch (error) {
    Logger.warn('Auth connection check failed:', error);
  }

  try {
    // Check Firestore connection by attempting to get a non-existent document
    if (db) {
      const { doc, getDoc } = await import('firebase/firestore');
      await getDoc(doc(db, 'connection-test', 'test'));
      status.firestore = true;
    }
  } catch (error) {
    // This is expected for a non-existent document, but it confirms connection
    if (error.code !== 'permission-denied') {
      Logger.warn('Firestore connection check failed:', error);
    } else {
      status.firestore = true;
    }
  }

  try {
    // Check Storage connection
    if (storage) {
      const { ref, getMetadata } = await import('firebase/storage');
      try {
        await getMetadata(ref(storage, 'connection-test'));
      } catch (error) {
        // Even if the file doesn't exist, a successful error response indicates connection
        status.storage = true;
      }
    }
  } catch (error) {
    Logger.warn('Storage connection check failed:', error);
  }

  return status;
};

/**
 * Firebase Authentication instance
 * Used for user login, registration, and session management
 * 
 * @example
 * import { auth } from './firebase/config';
 * import { signInWithEmailAndPassword } from 'firebase/auth';
 * 
 * const login = async (email, password) => {
 *   await signInWithEmailAndPassword(auth, email, password);
 * };
 */
export { auth };

/**
 * Firestore Database instance
 * Used for storing user data, credit balances, transactions, and app data
 * 
 * @example
 * import { db } from './firebase/config';
 * import { doc, getDoc, updateDoc } from 'firebase/firestore';
 * 
 * const getUserCredits = async (userId) => {
 *   const userDoc = await getDoc(doc(db, 'users', userId));
 *   return userDoc.data()?.credits || 0;
 * };
 */
export { db };

/**
 * Firebase Storage instance
 * Used for uploading PDF files and storing converted CSV files
 * 
 * @example
 * import { storage } from './firebase/config';
 * import { ref, uploadBytes } from 'firebase/storage';
 * 
 * const uploadFile = async (file, path) => {
 *   const storageRef = ref(storage, path);
 *   await uploadBytes(storageRef, file);
 * };
 */
export { storage };

/**
 * Firebase Functions instance
 * Used for calling Cloud Functions (PDF processing, credit management, etc.)
 * 
 * @example
 * import { functions } from './firebase/config';
 * import { httpsCallable } from 'firebase/functions';
 * 
 * const deductCredits = httpsCallable(functions, 'deductCredits');
 * await deductCredits({ amount: 1, reason: 'PDF processing' });
 */
export { functions };

/**
 * Google Auth Provider instance
 * Used for Google sign-in authentication
 * 
 * @example
 * import { googleProvider } from './firebase/config';
 * import { signInWithPopup } from 'firebase/auth';
 * 
 * const signInWithGoogle = async () => {
 *   await signInWithPopup(auth, googleProvider);
 * };
 */
export { googleProvider };

/**
 * Firebase Analytics instance
 * Used for tracking user events and app analytics
 * 
 * @example
 * import { analytics } from './firebase/config';
 * import { logEvent } from 'firebase/analytics';
 * 
 * const trackEvent = (eventName, parameters) => {
 *   logEvent(analytics, eventName, parameters);
 * };
 */
export { analytics };

/**
 * Wait for Firebase Auth state to be ready
 * This ensures authentication persistence is properly initialized
 * @returns {Promise<User|null>} The current user or null
 */
export const waitForAuthState = () => {
  return authStatePromise;
};

/**
 * Check if user is authenticated and auth state is ready
 * @returns {Promise<boolean>} True if user is authenticated
 */
export const isUserAuthenticated = async () => {
  try {
    await waitForAuthState();
    return !!auth.currentUser;
  } catch (error) {
    console.error('Error checking authentication state:', error);
    return false;
  }
};

/**
 * Get current authenticated user with state check
 * @returns {Promise<User|null>} Current user or null
 */
export const getCurrentUser = async () => {
  try {
    await waitForAuthState();
    return auth.currentUser;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

/**
 * Check if auth is ready (for loading states)
 * @returns {boolean} True if auth state is determined
 */
export const isAuthStateReady = () => {
  return authStateReady;
};

/**
 * Default Firebase app instance
 * Used for advanced configuration and accessing other Firebase services
 */
export default app;

